import { call, query } from './client.ts'
import { decoded, detailPath } from './admin.ts'
import type {
  AgentDirectoryPage,
  AgentDirectoryQuery,
  DetectionQuery,
  DetectionStats,
  DetectionStatsQuery,
  LogHistoryQuery,
  LogPage,
  ProcessTimelineQuery,
  SeverityCounts,
  VolumeStats,
} from './admin.ts'
import {
  decodeDetectionPage,
  decodeLogDetail,
  decodeProcessTree,
  decodeTimelinePage,
} from '../sse/parse.ts'
import type { DetectionPage, LogDetail, LogRow, ProcessTimelinePage, ProcessTree } from '../sse/types.ts'
import type {
  CompanyResponse,
  CreateTenantUserRequest,
  MachineQuery,
  MachineResponse,
  PagedResponse,
  UpdateUserRequest,
  UserQuery,
  UserResponse,
} from './types.ts'

/**
 * A superfície do CLIENTE (`/api/tenant`, §5).
 *
 * A empresa vem SEMPRE do token (`tenant_id`). Nenhuma função daqui aceita
 * empresa: os tipos de query tiram `tenantId`/`companyId`, e cada query string é
 * montada campo a campo, para que nem um objeto com a chave sobrando a leve.
 *
 * Corpo misto, igual ao equivalente da Black Ghost (§1.1): telemetria, agentes,
 * estatísticas e resumo em snake_case (os DTOs de `/api/admin`); máquinas,
 * empresa e usuários em camelCase (os de `/api/v1`). Autenticação, renovação no
 * 401 e tradução de erro são as do `client.ts`.
 */
export const TENANT_BASE = '/api/tenant'

/** `GET /api/tenant/stream/logs` — mesmos eventos de `/api/admin/stream/logs` (§5.11). */
export const TENANT_LOG_STREAM_URL = `${TENANT_BASE}/stream/logs`

// ── tipos próprios ───────────────────────────────────────────────────────────

export type TenantLogQuery = Omit<LogHistoryQuery, 'tenantId'>
export type TenantDetectionQuery = Omit<DetectionQuery, 'tenantId'>
export type TenantAgentQuery = Omit<AgentDirectoryQuery, 'tenantId'>
export type TenantDetectionStatsQuery = Omit<DetectionStatsQuery, 'tenantId'>
/** Sem `companyId` nem `includeDeleted`: máquina removida nunca aparece ao cliente (§5.9). */
export type TenantMachineQuery = Omit<MachineQuery, 'companyId' | 'includeDeleted'>
export type TenantUserQuery = Omit<UserQuery, 'companyId' | 'scope'>

/** Contagem de agentes por estado da varredura de silêncio. */
export interface TenantAgentCounts {
  total: number
  connected: number
  anomalous_silence: number
  expected_offline: number
  never_connected: number
  unknown: number
}

/** `GET /api/tenant/summary` (§5.2). Nenhum dado de plataforma. */
export interface TenantSummary {
  server_time: string
  tenant_id: string
  tenant_name: string | null
  agents: TenantAgentCounts
  detections_last_24h: SeverityCounts
  events_total: number
  directory_captured_at: string
  /** A última fotografia do diretório falhou: marque, não esconda. */
  directory_stale: boolean
}

// ── recursos ─────────────────────────────────────────────────────────────────

const opts = (signal?: AbortSignal) => ({ base: TENANT_BASE, signal })

export const tenant = {
  /** O cadastro da própria empresa (camelCase). */
  company: (signal?: AbortSignal) => call<CompanyResponse>('/company', opts(signal)),

  /** Duas consultas leves por chamada: poll de 30–60 s. */
  summary: (signal?: AbortSignal) => call<TenantSummary>('/summary', opts(signal)),

  agents: (params: TenantAgentQuery, signal?: AbortSignal) =>
    call<AgentDirectoryPage>(
      `/agents${query({ page: params.page, pageSize: params.pageSize, search: params.search })}`,
      opts(signal),
    ),

  logs: (params: TenantLogQuery, signal?: AbortSignal) =>
    call<LogPage>(
      `/logs${query({
        endpointId: params.endpointId,
        eventType: params.eventType,
        source: params.source,
        nativeId: params.nativeId,
        processEntityId: params.processEntityId,
        eventAction: params.eventAction,
        userName: params.userName,
        eventModule: params.eventModule,
        processName: params.processName,
        serviceName: params.serviceName,
        fileExtension: params.fileExtension,
        dnsQuestion: params.dnsQuestion,
        relatedIp: params.relatedIp,
        relatedUser: params.relatedUser,
        relatedHash: params.relatedHash,
        from: params.from,
        to: params.to,
        cursor: params.cursor,
        pageSize: params.pageSize,
      })}`,
      opts(signal),
    ),

  /** `/logs/{id}` ou `/logs/resolve`, como no console. Outra empresa = `404 log_not_found`. */
  logDetail: (row: LogRow, signal?: AbortSignal): Promise<LogDetail> =>
    call<unknown>(detailPath(row), opts(signal)).then(decoded(decodeLogDetail)),

  detections: (params: TenantDetectionQuery, signal?: AbortSignal): Promise<DetectionPage> =>
    call<unknown>(
      `/detections${query({
        endpointId: params.endpointId,
        processEntityId: params.processEntityId,
        severity: params.severity,
        ruleId: params.ruleId,
        responseAction: params.responseAction,
        responseStatus: params.responseStatus,
        isolatedMachines: params.isolatedMachines === true ? true : undefined,
        from: params.from,
        to: params.to,
        cursor: params.cursor,
        pageSize: params.pageSize,
      })}`,
      opts(signal),
    ).then(decoded(decodeDetectionPage)),

  /** Outra empresa = `404 process_not_found`; parentes de outra empresa vêm omitidos. */
  processTree: (entityId: string, signal?: AbortSignal): Promise<ProcessTree> =>
    call<unknown>(`/processes/${encodeURIComponent(entityId)}`, opts(signal)).then(
      decoded(decodeProcessTree),
    ),

  processTimeline: (
    entityId: string,
    params: ProcessTimelineQuery,
    signal?: AbortSignal,
  ): Promise<ProcessTimelinePage> =>
    call<unknown>(
      `/processes/${encodeURIComponent(entityId)}/timeline${query({
        from: params.from,
        to: params.to,
        eventType: params.eventType,
        cursor: params.cursor,
        pageSize: params.pageSize,
      })}`,
      opts(signal),
    ).then(decoded(decodeTimelinePage)),

  /** No máximo a própria empresa em `tenants`. Padrão 24 h, teto de 31 dias. */
  detectionStats: (params: TenantDetectionStatsQuery = {}, signal?: AbortSignal) =>
    call<DetectionStats>(
      `/stats/detections${query({ from: params.from, to: params.to })}`,
      opts(signal),
    ),

  /** Acumulado; `endpoints` sempre preenchido (agentes da empresa). */
  volumeStats: (signal?: AbortSignal) => call<VolumeStats>('/stats/volume', opts(signal)),

  /** Somente leitura (camelCase). Outra empresa = `404 machine_not_found`. */
  machines: {
    list: (params: TenantMachineQuery, signal?: AbortSignal) =>
      call<PagedResponse<MachineResponse>>(
        `/machines${query({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          isolated: params.isolated,
        })}`,
        opts(signal),
      ),

    get: (id: string, signal?: AbortSignal) =>
      call<MachineResponse>(`/machines/${encodeURIComponent(id)}`, opts(signal)),
  },

  /** Exige `tenant_admin`; `tenant_user` recebe 403 em todas. Só a própria empresa. */
  users: {
    list: (params: TenantUserQuery, signal?: AbortSignal) =>
      call<PagedResponse<UserResponse>>(
        `/users${query({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          role: params.role,
          includeInactive: params.includeInactive,
          includeDeleted: params.includeDeleted,
        })}`,
        opts(signal),
      ),

    get: (id: string, signal?: AbortSignal) =>
      call<UserResponse>(`/users/${encodeURIComponent(id)}`, opts(signal)),

    /** Vai sempre para a empresa do token: o corpo não tem `companyId`. */
    create: (body: CreateTenantUserRequest) =>
      call<UserResponse>('/users', { base: TENANT_BASE, method: 'POST', body: tenantUserBody(body) }),

    update: (id: string, body: UpdateUserRequest) =>
      call<UserResponse>(`/users/${encodeURIComponent(id)}`, {
        base: TENANT_BASE,
        method: 'PUT',
        body: { email: body.email, displayName: body.displayName, roles: body.roles, isActive: body.isActive },
      }),

    resetPassword: (id: string) =>
      call<void>(`/users/${encodeURIComponent(id)}/password`, { base: TENANT_BASE, method: 'PUT' }),

    remove: (id: string) =>
      call<void>(`/users/${encodeURIComponent(id)}`, { base: TENANT_BASE, method: 'DELETE' }),
  },
}

/** Campo a campo: um objeto que chegue com `companyId` sobrando não o leva ao servidor. */
function tenantUserBody(body: CreateTenantUserRequest): CreateTenantUserRequest {
  return {
    username: body.username,
    email: body.email,
    displayName: body.displayName,
    roles: body.roles,
  }
}
